import {
    ListView as AnvilListView,
    ListViewProps as AnvilListViewProps,
    Flex,
    Icon,
} from '@servicetitan/anvil2';
import { Controller, get, useFormContext } from 'react-hook-form';
import { ComponentProps, createContext, useContext } from 'react';
import Error from '@servicetitan/anvil2/assets/icons/material/round/warning.svg';

// Create a context to store the field name
interface ListViewContextType {
    name: string;
}

const ListViewContext = createContext<ListViewContextType | undefined>(undefined);

// Define a base interface for items to ensure they have a value property
interface ListViewItemWithValue {
    value: unknown;
}

// Define ItemType locally to match Anvil2's internal type
// ItemType<T> = T & { label: string; disabled?: boolean }
type ItemType<T> = T & {
    label: string;
    disabled?: boolean;
};

export type ListViewProps<T extends ListViewItemWithValue> = AnvilListViewProps<T> & {
    name: string;
    required?: boolean;
    onChange?: (value: unknown[]) => void;
};

export const ListView = <T extends ListViewItemWithValue>({
    name,
    onChange,
    ...props
}: ListViewProps<T>) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    // Get any validation errors for this field
    const error = get(errors, name);

    return (
        <ListViewContext.Provider value={{ name }}>
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <>
                        <AnvilListView<T>
                            {...props}
                            onSelectionChange={(items: ItemType<T>[] | string[]) => {
                                // Handle empty selection (deselect all)
                                if (items.length === 0) {
                                    field.onChange([]);
                                    onChange?.([]);
                                    return;
                                }

                                // Handle non-empty selections based on callback signature
                                if (typeof items[0] === 'object') {
                                    // Object array: extract values from items
                                    const typedItems = items as ItemType<T>[];
                                    field.onChange(typedItems.map(item => item.value));
                                    onChange?.(typedItems.map(item => item.value));
                                } else {
                                    // String array: use items directly as values
                                    field.onChange(items);
                                    onChange?.(items);
                                }
                            }}
                        />
                        {error && (
                            <Flex
                                gap="1"
                                justifyContent="flex-start"
                                alignItems="center"
                                style={{ color: 'var(--a2-foreground-color-danger)' }}
                            >
                                <Icon inherit aria-hidden svg={Error} />
                                {error.message}
                            </Flex>
                        )}
                    </>
                )}
            />
        </ListViewContext.Provider>
    );
};

// Create custom components to forward context properly
type ListViewOptionProps = ComponentProps<typeof AnvilListView.Option>;

const ListViewOption = (props: ListViewOptionProps) => {
    const context = useContext(ListViewContext);

    if (!context && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('ListViewOption must be used within a ListView component');
    }

    return <AnvilListView.Option {...props} />;
};

// Assign subcomponents to the ListView component
ListView.Option = ListViewOption;
ListView.OptionCell = AnvilListView.OptionCell;
